import type { DeltaBase, MetricValue } from '@ops/contracts'
import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Button } from './Button'
import { Card } from './Card'
import { DeltaTag } from './DeltaTag'
import { EmptyMetricState } from './EmptyMetricState'
import { HealthDot } from './HealthDot'
import { KpiValor } from './KpiValor'
import { ProvenanceBadge } from './ProvenanceBadge'
import { Sparkline } from './Sparkline'

/**
 * Tarjeta de KPI compacta. Es la unidad de la fila de métricas sanas: el
 * protagonista del período va en `HeroKpi`, no acá.
 *
 * Con `metric.valor === null` la tarjeta **no muestra "—" ni cero**: muestra
 * `EmptyMetricState`, que dice que falta y ofrece cargarlo.
 */
export interface KpiCardProps {
  metric: MetricValue
  /** Base del delta. Sin base el delta se publica avisando que falta. */
  base?: DeltaBase | null
  /** Con esto el delta se tiñe según si crecer es bueno para esta métrica. */
  direccion?: 'higher_better' | 'lower_better'
  /** Ausente = el rol no puede cargar datos: la tarjeta vacía no ofrece acción. */
  onLoadData?: () => void
  /** Ausente = no hay drawer de detalle para esta métrica. */
  onOpenDetail?: () => void
  /** Cobertura: el `RecordChip` del seguimiento que ya cubre este frente. */
  cobertura?: ReactNode
  /** false quita el sparkline, para filas muy densas. */
  sparkline?: boolean
  /**
   * Nivel del heading del nombre. 3 por defecto, mismo patrón que `Section`.
   *
   * Hardcodearlo rompía el outline de la página que pone una fila de tarjetas
   * antes de su primera `Section`: el `<h1>` saltaba a `<h3>` y los `<h3>`
   * quedaban *antes* del único `<h2>`, colgando de nada.
   */
  nivel?: 2 | 3 | 4
  /** Reloj compartido de la pantalla, para que la frescura sea consistente. */
  now?: Date
  className?: string
}

export function KpiCard({
  metric,
  base,
  direccion,
  onLoadData,
  onOpenDetail,
  cobertura,
  sparkline = true,
  nivel = 3,
  now,
  className,
}: KpiCardProps) {
  const sinDato = metric.valor === null
  const haySerie = metric.sparkline.some((punto) => punto !== null)
  const Heading = `h${nivel}` as const

  return (
    <Card
      as="section"
      ariaLabel={metric.nombre}
      severidad={metric.health}
      padding="sm"
      className={cx('flex flex-col gap-2', className)}
    >
      <header className="flex items-start justify-between gap-2">
        <Heading className="rotulo min-w-0 truncate" data-testid="kpi-nombre">
          {metric.nombre}
        </Heading>
        {onOpenDetail !== undefined ? (
          <Button variante="sutil" onClick={onOpenDetail} className="h-5 shrink-0 px-1">
            Ver detalle
            <span className="sr-only"> de {metric.nombre}</span>
          </Button>
        ) : null}
      </header>

      <div className="flex items-end justify-between gap-3">
        {metric.valor === null ? (
          <EmptyMetricState
            metricNombre={metric.nombre}
            onLoadData={onLoadData}
            size="md"
          />
        ) : (
          <KpiValor valor={metric.valor} unidad={metric.unidad} />
        )}

        {sparkline && haySerie ? (
          <Sparkline
            valores={metric.sparkline}
            health={metric.health}
            className="mb-1"
          />
        ) : null}
      </div>

      {!sinDato ? (
        <DeltaTag
          deltaPct={metric.deltaPct}
          base={base}
          unidad={metric.unidad}
          direccion={direccion}
          size="sm"
        />
      ) : null}

      {metric.benchmark !== null ? (
        <p className="text-micro text-muted-strong">{metric.benchmark}</p>
      ) : null}

      <footer className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        <HealthDot health={metric.health} size="sm" />
        <ProvenanceBadge provenance={metric.provenance} now={now} />
      </footer>

      {cobertura !== undefined ? (
        <div className="border-t border-border pt-2">{cobertura}</div>
      ) : null}
    </Card>
  )
}
